import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import { CircularProgress, Collapse, Dialog, DialogContent, Grid, Stack, TextField, Typography } from "@mui/material";
import Button from '@mui/material/Button';
import React, { useRef, useState } from "react";
import { Controller, useForm } from 'react-hook-form';
import { connect } from "react-redux";
import { OptionItem, PageData } from "../../../models";
import { Path as RoutePath } from "../../../path";
import { ApplicationState, FetchState, actionCreators, selectors } from "../../../store";
import palette from '../../../theme/palette';
import AlertDialog from '../../common/AlertDialog';
import FetchHelper from '../../FetchHelper';
import AddDeviceIdAllocation from './AddDeviceIdAllocation';
import DeviceIdAllocationGrid from './DeviceIdAllocationGrid';
import EditDeviceIdAllocation from './EditDeviceIdAllocation';

export const PageSizeList = [
    { value: '10', label: '10', attribute: '', },
    { value: '50', label: '50', attribute: '', },
    { value: '100', label: '100', attribute: '', },
    { value: '500', label: '500', attribute: '', },
    { value: '1000', label: '1000', attribute: '', },
    { value: '5000', label: '5000', attribute: '', },
    { value: '10000', label: '10000', attribute: '', },
] as OptionItem[];

// -------------
// FETCH ID
const fetchId = "DEVICE_ID_ALLOCATION";
const ADD_DEVICE_ID_ALLOCATION = "ADD_DEVICE_ID_ALLOCATION"; 
const EDIT_DEVICE_ID_ALLOCATION = "EDIT_DEVICE_ID_ALLOCATION";
const DELETE_DEVICE_ID_ALLOCATION = "DELETE_DEVICE_ID_ALLOCATION";

const formId = "DEVICE_SEARCH_FORM";
const menusFetchId = 'MENUS_FETCH';

// 検索フォーム
interface SearchItemForm {
    keyword: string;
    page:number,
    limit:number,
}

interface DeviceIdAllocationModel {
    mainid:string;
    ip_address:string;
    created:string;
    updated:string;
    isSelect: boolean;
}

// 初期値設定
const initialValue: SearchItemForm = {
    limit: 50,
    page: 0,
    keyword: '',
}

const initialPageData: PageData = {
    rowFrom: '0',
    rowTo: '0',
    totalRow: '0',
    currPage: '0',
    totalPage: '0',
};

// -------------
// Props
export type DeviceIdAllocationListProps = ReturnType<typeof mapStateToProps> &
    typeof mapDispatchToProps;

// -------------

// Component
export const DeviceIdAllocationList = (props: DeviceIdAllocationListProps) => {
    const {onSearch, onDelete, onDeleteSuccess, onDeleteError, onCreate, onCreateSuccess, onCreateError, onEdit, onEditSuccess, onEditError, onError,} = props;

    localStorage.setItem('lastActiveFormPath', RoutePath.DeviceIdAllocation);
    localStorage.setItem('title', '装置ID引当');

    const { control, handleSubmit, setValue, getValues } = useForm<SearchItemForm>({
        criteriaMode: "all",
        defaultValues: initialValue,
    });
    const gridRef = useRef() as any;
    
    const [colState, setColState] = useState<any>();
    const [loadingFlag, setLoadingFlag] = React.useState(false);
    const [openSearch, setOpenSearch] = React.useState(false);
    const [disableEdit, setDisableEdit] = React.useState(true);
    const [disableDelete, setDisableDelete] = React.useState(true);
    const [listData, setListData] = React.useState<DeviceIdAllocationModel[]>([]);
    const [pageData, setPageData] = React.useState(initialPageData);
    const [openAddDialog, setOpenAddDialog] = React.useState(false);
    const [openEditDialog, setOpenEditDialog] = React.useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);
    const [title, setTitle] = React.useState("");
    const [message, setMessage] = React.useState("");

    React.useEffect(() => {
        handleSearchSubmit(getValues());
    },[]);       

    /**
     * 検索ボタン押下時の処理
     * ページを0にリセットし、現在のフォーム値で検索を実行
    */
    const handleSearch = (data: any) => {
        setValue("page", 0)
        handleSearchSubmit(getValues())
    };

    /**
     * 検索実行メイン処理
     * フォーム値から検索パラメータを構築し、API呼び出しをトリガー
     */
    const handleSearchSubmit = (value: SearchItemForm) => {
        if (gridRef && gridRef.current) {
            setColState(gridRef.current.saveState());
        }
        const params = new Map<string, any>();
        if (value.limit) params.set("limit", value.limit);
        if (value.page) params.set("page", value.page - 1);
        if (value.keyword) params.set("keyword", value.keyword);
        setLoadingFlag(true);
        onSearch(params);
    }

    // ハンドルオープン
    const handleOpen = () => {
        setOpenSearch((prev: boolean) => !prev);
    };

    // 検索条件をクリアします
    const handleClearSeachCondition = () => {
        setValue("keyword", '')
    };

    /**
     * 検索API成功時のコールバック
     * 受信データをグリッドに反映し、ページネーション情報を更新
     */
    const handleSuccessGetAll = (data: any) => {
        setLoadingFlag(false);
        if (data && data.results){
            // グリッドに行データを設定
            if (gridRef && gridRef.current) {
                gridRef.current.setRowData(handleGetData(data.results))
            }
            setListData(() => [...handleGetData(data.results)]);
            const totalCount = data.total_count;
            const pageSize = getValues('limit');
            const page = Number(data.page) + 1;
            const totalPage = Math.ceil(totalCount / pageSize);
            const rowFrom = (pageSize * page - pageSize) + 1;
            const rowTo = (pageSize * page) > totalCount ? totalCount : (pageSize * page);
            const updateData: PageData = {
                rowFrom: rowFrom.toLocaleString(),
                rowTo: rowTo.toLocaleString(),
                totalRow: totalCount.toLocaleString(),
                currPage: page.toLocaleString(),
                totalPage: totalPage.toLocaleString(),
            };
            setPageData(updateData);
            // 初期状態：編集・削除ボタンを無効化
            setDisableEdit(true);
            setDisableDelete(true);
        }
    }

    /**
     * APIレスポンスデータをグリッド用に変換
     * 各行に選択状態フラグ `isSelect` を追加
    */
    const handleGetData = (data: any) => {
        var result = [] as any[];        
        Object.values(data)?.forEach((element: any) => {
            let deviceIdAllocation = {...element} as any;
            deviceIdAllocation.isSelect = false;
            result.push(deviceIdAllocation);
        });
        return result;
    };

    /**
     * APIエラー共通処理
     */
    const handleError = ( success:any, data:any, error:any ) => {
        setLoadingFlag(false);
        onError(success, data, error);
    }

    /**
     * 新規追加ダイアログを開く
    */
    const handleClickAddDialog = () => {
        setOpenAddDialog(true);
    }

    /**
     * 編集ダイアログを開く（選択行が1件の場合のみ有効）
    */
    const handleEdit = () => {
        setOpenEditDialog(true);
    }

    // ページ数変更時にAPI検索する
    const handleChangePageData = (name: any, value: any) => {
        // 値変更
        setValue(name, value);
        if (name === 'limit') {
            setValue("page", 0);
        }
        handleSearchSubmit(getValues());
    };

    // 検索正常終了時
    const handleSelect = (data: any) => {
        if (gridRef && gridRef.current) {
            setColState(gridRef.current.saveState());
        }
        let list_data = listData.filter((item) => item.ip_address === data.ip_address);
        list_data[0].isSelect = !list_data[0].isSelect;
        if (gridRef && gridRef.current) {
            gridRef.current.setSelectRowData(data.ip_address);
        }
        let selectCount = listData.filter((item) => item.isSelect === true).length;

        if (selectCount === 1) {
            setDisableEdit(false);
            setDisableDelete(false);
        } else if (selectCount > 0) {
            setDisableEdit(true);
            setDisableDelete(false);
        }
        else {
            setDisableEdit(true);
            setDisableDelete(true);
        }
    };

    /**
     * 新規追加API成功時
     */
    const handleSuccessAdd = (data:any) => {
        if(data && data.result_code == 0){
            setOpenAddDialog(false);
            onCreateSuccess();
            handleSearchSubmit(getValues());
            setDisableEdit(true);
            setDisableDelete(true);
        }else{
            setLoadingFlag(false);
            onCreateError();
        }
    }

    /**
     * 新規追加API失敗時
     */
    const handleErrorAdd = (success:any, data:any, error:any) => {
        setOpenAddDialog(false);
        setLoadingFlag(false);
        onCreateError();
    }

    /**
     * 編集API成功時
    */
    const handleSuccessEdit = (data:any) => {
        if(data && data.result_code == 0){
            setOpenEditDialog(false);
            onEditSuccess();
            handleSearchSubmit(getValues())
            setDisableEdit(true);
            setDisableDelete(true);
        }else{
            onEditError();
            setLoadingFlag(false);
        }
    }

    /**
     * 編集API失敗時
     */
    const handleErrorEdit = (success:any, data:any, error:any) => {
        setOpenEditDialog(false);
        setLoadingFlag(false);
        onEditError();
    }

    //削除の処理
    const handleDelete = (data: any) => {
        let selectedData = listData.filter((item) => item.isSelect === true);
        if (selectedData.length === 0) {
            return;
        }
        let ipAdresses = selectedData.map((obj: any) => obj.ip_address).join(', ');
        const msg = "装置ID引当情報：" + ipAdresses + " を削除しますか？";
        setTitle("装置ID引当情報削除");
        setMessage(msg);
        setOpenDeleteDialog(true);
    }

    /**
     * 削除確認ダイアログで「OK」押下時
     * 選択された全項目を削除APIに送信
     */
    const handleDeleteOK = () => {
        setLoadingFlag(true);
        listData.filter((item) => item.isSelect === true).forEach((item) => {
            onDelete(item.ip_address);
        })
    }

    /**
     * 削除確認ダイアログで「キャンセル」押下時
     */
    const handleDeleteCancel = () => {
        setOpenDeleteDialog(false);
    }

    /**
     * 削除API成功時
     */
    const handleSuccessDelete = ( data:any) => {
        if( data && data.result_code == 0){
            setOpenDeleteDialog(false);
            onDeleteSuccess();
            handleSearchSubmit(getValues());
            setDisableEdit(true);
            setDisableDelete(true);
        }else{
            setOpenDeleteDialog(false);
            onDeleteError();
            setLoadingFlag(false);
        }
    }

    /**
     * 削除API失敗時
     */
    const handleErrorDelete = ( success:any, data:any, error:any) => {
        onDeleteError();
        setLoadingFlag(false);
        setOpenDeleteDialog(false);
    }

    return (
        <>
            {openDeleteDialog &&
                <AlertDialog 
                    title={title} 
                    message={message} 
                    isOpen={true} 
                    onOK={handleDeleteOK} 
                    onCancel={handleDeleteCancel} 
                />
            }
            {openAddDialog &&
                <Dialog open={openAddDialog} maxWidth={"md"} fullWidth style={{ zIndex: 1200 }}>
                    <DialogContent sx={{ pb: 1.5, pt:0.5 }}>
                        <AddDeviceIdAllocation onOK={(params:any) => {
                            setLoadingFlag(true);
                            onCreate(params);
                        }} onCancel={
                            () => {
                                setOpenAddDialog(false);
                            }
                        } />
                    </DialogContent>
                </Dialog>
            }
            {
                openEditDialog &&
                <Dialog open={openEditDialog} maxWidth={"md"} fullWidth style={{ zIndex: 1200 }}>
                    <DialogContent sx={{ pb: 1.5,pt:0.5 }}>
                        <EditDeviceIdAllocation 
                            onOK={(params:any) => {
                                setLoadingFlag(true);
                                onEdit(params);
                            }} 
                            onCancel={() => {
                                setOpenEditDialog(false);
                            }}
                            initialData={listData.find((item:any) => item.isSelect == true)}
                        />
                    </DialogContent>
                </Dialog>
            }

            {loadingFlag && (
                <div style={{ top: "0px", left: '0px', position: "fixed", zIndex: 1500, width: '100%', height: '100%', padding: '50vh 50% 50% 50%', background: '#00000030' }}>
                    <CircularProgress />
                </div>
            )}
            <form onSubmit={handleSubmit(handleSearch)}>
                <Stack spacing={1} mb="10px" style={{ border: openSearch ? "2px solid gray" : '', borderRadius: "15px" }}>
                    <div style={{ cursor: 'pointer', display: 'inline-block', marginLeft: '10px' }} onClick={handleOpen}>
                        <div style={{ display: 'inline-block' }}>
                            <Typography variant="h6" >検索条件</Typography>
                        </div>
                        <div style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                            {
                                openSearch ? <ArrowDropDownIcon /> : <ArrowRightIcon />
                            }
                        </div>
                    </div>
                    <div style={{ marginLeft: '10px', marginRight: '10px', marginBottom: "10px" }}>
                        <Collapse in={openSearch} timeout="auto" unmountOnExit>
                            <Grid
                                container
                                justifyContent="flex-start"
                                alignItems={"end"}
                                spacing={1}
                                direction="row"
                                style={{ alignItems: 'center' }}
                            >
                                <Grid item>
                                    <Stack direction="row">
                                        <Controller
                                            name="keyword"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    type="text"
                                                    label="検索キーワード"
                                                    size="small"
                                                    style={{ background: palette.background.paper }}
                                                />
                                            )}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item>
                                    <Button variant="outlined" style={{ background: palette.background.paper }} onClick={handleClearSeachCondition}>クリア</Button>
                                </Grid>
                                <Grid item >
                                    <Button variant="contained" onClick={handleSearch}>検索</Button>
                                </Grid>
                            </Grid>
                        </Collapse>
                    </div>
                </Stack>
            </form>
            <Stack direction={"row"} justifyContent={"flex-end"}>
                <Stack direction={{ xs: 'column', sm: 'row' }} ml={5} spacing={1} mb={1}>
                    <Button variant="contained" size="medium" onClick={handleClickAddDialog}>{<AddIcon />}</Button>
                    <Button variant="contained" size="medium" onClick={handleEdit} disabled={disableEdit}>{<EditIcon />}</Button>
                    <Button variant="contained" size="medium" onClick={handleDelete} disabled={disableDelete}>{<ClearIcon />} </Button>
                </Stack>
            </Stack>
            <div>       
                <DeviceIdAllocationGrid
                    ref={gridRef}
                    colState={colState}
                    onSelect={handleSelect}
                    openSearch={openSearch}
                    pageSize={getValues('limit')}
                    pageData={pageData}
                    onChangeSearchCondition={handleChangePageData}
                /> 
            </div>

            <FetchHelper fetchId={fetchId} onComplete={(success, data, error) =>
                success ? handleSuccessGetAll(data) : handleError(success, data, error)} />
            <FetchHelper fetchId={ADD_DEVICE_ID_ALLOCATION} onComplete={(success, data, error) =>
                success ? handleSuccessAdd(data) : handleErrorAdd(success, data, error)} />
            <FetchHelper fetchId={EDIT_DEVICE_ID_ALLOCATION} onComplete={(success, data, error) =>
                success ? handleSuccessEdit(data) : handleErrorEdit(success, data, error)} />
            <FetchHelper fetchId={DELETE_DEVICE_ID_ALLOCATION} onComplete={(success, data, error) =>
                success ? handleSuccessDelete(data) : handleErrorDelete(success, data, error)} />
        </>
    );
};

const mapStateToProps = (state: ApplicationState) => ({
    fetchStateMenu: selectors.getFetchState(
        state,
        menusFetchId
    ) as FetchState,
    formState: selectors.getFormState(state, formId),
});

const mapDispatchToProps = {
    onGetAuthRole: () => actionCreators.fetch(menusFetchId, `/master/udpgw-ipidmap/get`, 'GET', null),
    onSearch: (params: { [key: string]: any }) => actionCreators.fetch(fetchId, `/master/udpgw-ipidmap/get`, "GET", params),
    onChangePageSize: (pageSize: number) => actionCreators.setPageSize(pageSize),
    onCreate: (params:any) => actionCreators.fetch(ADD_DEVICE_ID_ALLOCATION,`/master/udpgw-ipidmap/add`, "POST",params,false,true),
    onCreateSuccess: () =>
        actionCreators.showMessage({
            type: "info",
            title: "装置ID引当情報追加",
            body: "装置ID引当情報を登録しました。",
        }),
    onCreateError: () =>
        actionCreators.showMessage({
            type: "error",
            title: "装置ID引当情報追加",
            body: "装置ID引当情報が登録出来ませんでした。",
        }),
    onEdit: (params:any) => actionCreators.fetch(EDIT_DEVICE_ID_ALLOCATION,`/master/udpgw-ipidmap/edit`, "POST",params,false,true),
    onEditSuccess: () =>
        actionCreators.showMessage({
            type: "info",
            title: "装置ID引当情報編集",
            body: "装置ID引当情報を更新しました。",
        }),
    onEditError: () =>
        actionCreators.showMessage({
            type: "error",
            title: "装置ID引当情報編集",
            body: "装置ID引当情報が更新出来ませんでした。",
        }),
    onError: (success: boolean, data: any, error: any) =>
        actionCreators.showMessage({
            type: "error",
            title: "装置ID引当情報検索",
            body: "装置ID引当情報を検索できません。" + error,
        }),
    onDelete: (ip_address: string) => actionCreators.fetch('DELETE_DEVICE_ID_ALLOCATION', `/master/udpgw-ipidmap/delete?ip_address=${ip_address}`, "GET", null),
    onDeleteSuccess: () =>
        actionCreators.showMessage({
            type: "info", 
            title: "装置ID引当情報削除",
            body: "装置ID引当情報を削除しました。",
        }),
    onDeleteError: () =>
        actionCreators.showMessage({
            type: "error",
            title: "装置ID引当情報削除",
            body: "装置ID引当情報を削除出来ませんでした。",
        }),
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(DeviceIdAllocationList as any);
